import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BadgeCheck, CalendarClock, ChevronLeft, MoreHorizontal } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { ExpandingIconButton } from '@/components/ui/expanding-icon-button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { displayDate } from '@/lib/dates'
import { getJSON, postJSON } from '@/lib/api'
import { RenewalPipelineActionDialogs } from '@/pages/RenewalPipelineActionDialogs'
import type { PipelineAction, PipelineRow } from '@/pages/RenewalPipelinePage.types'

type ArmingStatus = 'Active' | 'Pending-Renewal'

interface ArmingRow {
  membership_id: number
  person_id: number
  invoice_id: number | null
  name: string
  status: ArmingStatus
  level: string
  open_amount: string
  open_amount_cents: number
  renewal_due_on: string
  overdue_since_on: string
  lapse_on: string | null
  hold_ends_on: string | null
  hold_reason: string | null
}

export interface ArmingResponse {
  renewal_arming: {
    auto_lapse_enabled: boolean
    enabled_on: string | null
    as_of: string
    overdue_members: ArmingRow[]
  }
}

type Resolution = { kind: 'held'; endsOn: string } | { kind: 'renewed' | 'suspended' }

const statusVariant: Record<ArmingStatus | 'Suspended', BadgeProps['variant']> = {
  Active: 'success',
  'Pending-Renewal': 'neutral',
  Suspended: 'warning',
}

export function RenewalArmingReviewPage({
  onCancel,
  onArmed,
  onOpenMember,
  currentAssociationDate,
}: {
  onCancel: () => void
  onArmed: () => void
  onOpenMember: (personId: number) => void
  currentAssociationDate?: string
}) {
  const [action, setAction] = React.useState<PipelineAction | null>(null)
  const [resolutions, setResolutions] = React.useState<Record<number, Resolution>>({})
  const queryClient = useQueryClient()
  const reviewQuery = useQuery({
    queryKey: ['renewal-arming'],
    queryFn: () => getJSON<ArmingResponse>('/api/v1/renewal_arming'),
    retry: false,
  })
  const arm = useMutation({
    mutationFn: () => postJSON<ArmingResponse>('/api/v1/renewal_arming', {}),
    onSuccess: onArmed,
  })
  const rows = reviewQuery.data?.renewal_arming.overdue_members ?? []

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="flex items-end justify-between gap-6 pb-2">
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mb-1 h-7 gap-1 px-2"
            onClick={onCancel}
          >
            <ChevronLeft className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
            Renewal settings
          </Button>
          <h1 className="text-heading-sm font-semibold text-foreground">Turn on auto-lapse</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" className="h-8 px-3" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            className="h-8 px-3"
            disabled={arm.isPending || reviewQuery.isLoading || reviewQuery.isError}
            onClick={() => arm.mutate()}
          >
            {arm.isPending ? 'Turning on…' : 'Turn on auto-lapse'}
          </Button>
        </div>
      </header>

      <p className="pb-4 text-body-sm text-muted-foreground">
        {reviewSummary(rows.length)} Each starts its grace countdown from today.
      </p>
      {arm.isError && <Alert className="mb-4">Auto-lapse could not be turned on.</Alert>}

      <div className="overflow-hidden rounded-md border border-border bg-card">
        {reviewQuery.isLoading ? (
          <EmptyState title="Loading arming review" className="min-h-52" />
        ) : reviewQuery.isError ? (
          <EmptyState title="Arming review unavailable" className="min-h-52" />
        ) : rows.length === 0 ? (
          <EmptyState title="No overdue members" className="min-h-52" />
        ) : (
          <ReviewTable
            rows={rows}
            resolutions={resolutions}
            onAction={setAction}
            onOpenMember={onOpenMember}
          />
        )}
      </div>

      <RenewalPipelineActionDialogs
        action={action}
        currentAssociationDate={currentAssociationDate}
        onClose={() => setAction(null)}
        onResolved={async (details) => {
          if (!action) return
          if (action.kind === 'record-payment') {
            const refreshed = await getJSON<ArmingResponse>('/api/v1/renewal_arming')
            if (paymentStillOverdue(refreshed, action.row.membership_id)) {
              queryClient.setQueryData(['renewal-arming'], refreshed)
              return
            }
          }
          const resolution = resolutionFor(action, details?.holdEndsOn)
          setResolutions((current) => ({ ...current, [action.row.membership_id]: resolution }))
        }}
      />
    </div>
  )
}

export function paymentStillOverdue(response: ArmingResponse, membershipId: number) {
  return response.renewal_arming.overdue_members.some((row) => row.membership_id === membershipId)
}

function ReviewTable({
  rows,
  resolutions,
  onAction,
  onOpenMember,
}: {
  rows: ArmingRow[]
  resolutions: Record<number, Resolution>
  onAction: (action: PipelineAction) => void
  onOpenMember: (personId: number) => void
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Member</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Open</TableHead>
          <TableHead>Overdue since</TableHead>
          <TableHead>Will lapse</TableHead>
          <TableHead className="w-72 min-w-72" aria-label="Row actions" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <ReviewRow
            key={row.membership_id}
            row={row}
            resolution={resolutions[row.membership_id]}
            onAction={onAction}
            onOpenMember={onOpenMember}
          />
        ))}
      </TableBody>
    </Table>
  )
}

function ReviewRow({
  row,
  resolution,
  onAction,
  onOpenMember,
}: {
  row: ArmingRow
  resolution?: Resolution
  onAction: (action: PipelineAction) => void
  onOpenMember: (personId: number) => void
}) {
  const pipelineRow = armingPipelineRow(row)
  const heldUntil = resolution?.kind === 'held' ? resolution.endsOn : row.hold_ends_on
  const status = resolvedStatus(row.status, resolution)
  const resolved = resolution?.kind === 'renewed' || resolution?.kind === 'suspended'

  return (
    <TableRow>
      <TableCell className="py-2">
        <div className="text-body-sm">{row.name}</div>
        <div className="text-caption text-muted-foreground">{row.level}</div>
      </TableCell>
      <TableCell className="py-2">
        <Badge variant={statusVariant[status]}>{status}</Badge>
      </TableCell>
      <TableCell className="py-2 text-right tabular-nums">
        {resolved ? '—' : row.open_amount}
      </TableCell>
      <TableCell className="py-2 tabular-nums">{displayDate(row.overdue_since_on)}</TableCell>
      <TableCell className="py-2">
        <LapseValue heldUntil={heldUntil} lapseOn={row.lapse_on} resolved={resolved} />
      </TableCell>
      <TableCell className="w-72 min-w-72 py-2 text-right">
        {!resolved && (
          <div className="flex min-w-72 items-center justify-end gap-0.5">
            <ExpandingIconButton
              icon={CalendarClock}
              label="Hold…"
              onClick={() => onAction({ kind: 'hold', row: pipelineRow })}
            />
            <ExpandingIconButton
              icon={BadgeCheck}
              label="Renew now"
              onClick={() => onAction(renewAction(pipelineRow))}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 px-0"
                  aria-label={`Actions for ${row.name}`}
                >
                  <MoreHorizontal className="size-4" strokeWidth={1.5} aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onAction(renewAction(pipelineRow))}>
                  Renew now
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onAction({ kind: 'hold', row: pipelineRow })}>
                  Hold…
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  destructive
                  onSelect={() => onAction({ kind: 'suspend', row: pipelineRow })}
                >
                  Suspend
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onOpenMember(row.person_id)}>
                  Open contact
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </TableCell>
    </TableRow>
  )
}

function LapseValue({
  heldUntil,
  lapseOn,
  resolved,
}: {
  heldUntil: string | null
  lapseOn: string | null
  resolved: boolean
}) {
  if (heldUntil) {
    return (
      <div>
        <Badge variant="neutral">Held</Badge>
        <div className="text-caption text-muted-foreground">until {displayDate(heldUntil)}</div>
      </div>
    )
  }
  if (resolved || !lapseOn) return <span className="text-muted-foreground">—</span>

  return <span className="tabular-nums">{displayDate(lapseOn)}</span>
}

function resolvedStatus(status: ArmingStatus, resolution?: Resolution) {
  if (resolution?.kind === 'renewed') return 'Active'
  if (resolution?.kind === 'suspended') return 'Suspended'

  return status
}

function reviewSummary(count: number) {
  return `${count} ${count === 1 ? 'member is' : 'members are'} past their due date.`
}

function renewAction(row: PipelineRow): PipelineAction {
  return row.invoice_id ? { kind: 'record-payment', row } : { kind: 'renew', row }
}

function resolutionFor(action: PipelineAction, holdEndsOn?: string): Resolution {
  if (action.kind === 'hold') return { kind: 'held', endsOn: holdEndsOn ?? '' }
  if (action.kind === 'suspend') return { kind: 'suspended' }
  return { kind: 'renewed' }
}

function armingPipelineRow(row: ArmingRow): PipelineRow {
  return {
    ...row,
    organization_id: null,
    contact_kind: 'person',
    renewal_due_on: row.renewal_due_on,
    paid_detail: null,
    days_until_lapse: null,
    autopay_state: 'Not enrolled',
    reachability: 'Reachable',
    reason: null,
    reason_kind: null,
    dismissible: false,
    lapsed_on: null,
    record_payment_available: row.invoice_id !== null,
    charge_now_available: false,
    renew_available: row.invoice_id === null,
    suspend_available: true,
  }
}
